import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, IconButton, Divider, CircularProgress,
  Popover, List, ListItemButton, ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ListAltIcon from '@mui/icons-material/ListAlt';
import RouteIcon from '@mui/icons-material/Route';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import {
  fetchAttractionWeather,
  type Attraction,
  type AttractionWeather,
} from '../../services/attractionService';
import { type TripPlan, type TripAttraction, TRANSPORT_OPTIONS } from '../../services/tripService';

interface Props {
  attraction: Attraction | null;
  position?: { x: number; y: number } | null;
  onClose: () => void;
  container?: Element | null;
  plans?: TripPlan[];
  onPlansChange?: (plans: TripPlan[]) => void;
  onOpenTripPanel?: () => void;
}

const CARD_W = 260;
const CARD_H = 440;
const TODAY = new Date().toISOString().slice(0, 10);

function getDayLabel(dateStr: string, index: number, lang: string): string {
  if (index === 0) return lang === 'en' ? 'Today' : '今';
  const d = new Date(dateStr + 'T12:00:00');
  if (lang === 'en') return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
  return ['日', '一', '二', '三', '四', '五', '六'][d.getDay()];
}

const AttractionDetailCard: React.FC<Props> = ({
  attraction, position, onClose, container,
  plans: propPlans = [], onPlansChange, onOpenTripPanel,
}) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const isZh = !lang.startsWith('en');

  const [weather, setWeather]               = useState<AttractionWeather | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError]     = useState(false);
  const [imgError, setImgError]             = useState(false);

  // Context menu
  const [ctxAnchor, setCtxAnchor] = useState<{ x: number; y: number } | null>(null);
  const [ctxPlanId, setCtxPlanId] = useState<string | null>(null); // null = plan list, string = date+transport form
  const [ctxDate, setCtxDate]     = useState(TODAY);
  const [ctxTransport, setCtxTransport] = useState('');
  const [ctxLoading, setCtxLoading] = useState(false);

  useEffect(() => {
    if (!attraction) return;
    setWeather(null);
    setImgError(false);
    setWeatherLoading(true);
    setWeatherError(false);
    fetchAttractionWeather(attraction.lat, attraction.lng)
      .then(w => { setWeather(w); setWeatherLoading(false); })
      .catch(() => { setWeatherError(true); setWeatherLoading(false); });
  }, [attraction?.id]);

  if (!attraction) return null;

  const name = isZh ? (attraction.nameZH || attraction.nameEN) : (attraction.nameEN || attraction.nameZH);
  const desc = isZh ? (attraction.descZH || attraction.descEN) : (attraction.descEN || attraction.descZH);
  const showImage = !!attraction.imageUrl && !imgError;

  const inPlanCount = propPlans.filter(p =>
    p.attractions?.some(a => a.id === attraction.id)
  ).length;

  let posStyle: React.CSSProperties;
  if (position) {
    const GAP = 14;
    let left = position.x + GAP;
    let top  = position.y - CARD_H / 2;
    if (left + CARD_W > window.innerWidth  - 8) left = position.x - CARD_W - GAP;
    if (left < 8)                               left = 8;
    if (top  + CARD_H > window.innerHeight - 8) top  = window.innerHeight - CARD_H - 8;
    if (top < 8)                                top  = 8;
    posStyle = { position: 'absolute', left, top };
  } else {
    posStyle = { position: 'absolute', bottom: 24, left: 24 };
  }

  const hasRain = weather?.forecast.some(d => d.precipitation > 0);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setCtxPlanId(null);
    setCtxDate(TODAY);
    setCtxTransport('');
    setCtxAnchor({ x: e.clientX, y: e.clientY });
  };

  const closeCtx = () => { setCtxAnchor(null); setCtxPlanId(null); setCtxLoading(false); };

  // When plan is selected, default date to plan's latest date (or today)
  const handleSelectPlan = (planId: string) => {
    const plan = propPlans.find(p => p.id === planId);
    const dates = (plan?.attractions.map(a => a.date) ?? []).sort();
    setCtxDate(dates.at(-1) ?? TODAY);
    setCtxTransport('');
    setCtxPlanId(planId);
  };

  const handleConfirmAdd = async () => {
    if (!ctxPlanId || !ctxDate || ctxLoading) return;
    setCtxLoading(true);

    const plan = propPlans.find(p => p.id === ctxPlanId);
    const dayAttrs = plan?.attractions.filter(a => a.date === ctxDate) ?? [];
    const order = dayAttrs.length + 1;

    let country: string | undefined;
    let city: string | undefined;
    try {
      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), 3000);
      const geoRes = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${attraction.lat}&lon=${attraction.lng}&zoom=10`,
        { headers: { 'Accept-Language': lang }, signal: controller.signal },
      );
      clearTimeout(tid);
      if (geoRes.ok) {
        const geo = await geoRes.json();
        country = geo.address?.country;
        city = geo.address?.city ?? geo.address?.town ?? geo.address?.village
             ?? geo.address?.municipality ?? geo.address?.county;
      }
    } catch { /* geocode failure is non-blocking */ }

    const tripAttraction: TripAttraction = {
      ...attraction,
      date: ctxDate,
      transport: ctxTransport || undefined,
      order,
      country,
      city,
    };
    const updated = propPlans.map(p => {
      if (p.id !== ctxPlanId) return p;
      const already = p.attractions?.some(a => a.id === attraction.id && a.date === ctxDate);
      return already ? p : { ...p, attractions: [...p.attractions, tripAttraction] };
    });
    onPlansChange?.(updated);
    closeCtx();
  };

  const currentPlan = propPlans.find(p => p.id === ctxPlanId);
  // Existing dates in the selected plan (for quick-select chips)
  const existingDates = Array.from(new Set(currentPlan?.attractions.map(a => a.date) ?? [])).sort();

  return (
    <>
      <Paper
        elevation={4}
        onContextMenu={handleContextMenu}
        sx={{
          ...posStyle,
          width: CARD_W, borderRadius: 3, zIndex: 10,
          background: 'rgba(15,25,50,0.92)', backdropFilter: 'blur(10px)',
          color: '#fff', overflow: 'hidden',
          maxHeight: CARD_H, display: 'flex', flexDirection: 'column',
        }}
      >
        <IconButton onClick={onClose} size="small" sx={{
          position: 'absolute', top: 6, right: 6, zIndex: 1,
          color: 'rgba(255,255,255,0.6)',
        }}>
          <CloseIcon fontSize="small" />
        </IconButton>

        {inPlanCount > 0 && (
          <div style={{
            position: 'absolute', top: 6, left: 6, zIndex: 1,
            background: 'rgba(155,89,182,0.85)', borderRadius: 4,
            padding: '1px 6px', fontSize: 10, color: '#fff',
          }}>
            ✓ {inPlanCount} {isZh ? '個計畫' : 'plan(s)'}
          </div>
        )}

        {showImage && (
          <img src={attraction.imageUrl} alt={name} onError={() => setImgError(true)}
            style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block' }} />
        )}

        <div style={{ padding: '10px 12px', overflowY: 'auto', flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, pr: 3, lineHeight: 1.3 }}>
            🏛️ {name}
          </Typography>
          {desc && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mt: 0.5 }}>
              {desc}
            </Typography>
          )}

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 1 }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
              📍 {attraction.lat.toFixed(2)}°, {attraction.lng.toFixed(2)}°
            </Typography>
            <a href={attraction.wikidataUrl} target="_blank" rel="noreferrer"
              style={{ color: '#7ec8ff', fontSize: 11, display: 'flex', alignItems: 'center', gap: 2 }}>
              {isZh ? '了解更多' : 'More info'} <OpenInNewIcon sx={{ fontSize: 11 }} />
            </a>
          </div>

          <Typography variant="caption" sx={{ color: 'rgba(255,140,66,0.55)', fontSize: 10, display: 'block', mt: 0.3 }}>
            {isZh ? '右鍵可加入旅程計畫' : 'Right-click to add to a trip plan'}
          </Typography>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 1 }} />

          {weatherLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
              <CircularProgress size={18} sx={{ color: '#4285f4' }} />
            </div>
          )}
          {weatherError && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', textAlign: 'center' }}>
              {isZh ? '天氣資料無法取得' : 'Weather unavailable'}
            </Typography>
          )}
          {weather && (
            <>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', mb: 0.8 }}>
                {isZh ? '本週天氣' : 'This Week'}
              </Typography>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', gap: '3px 0' }}>
                {weather.forecast.map((d, i) => (
                  <div key={`lbl-${d.date}`} style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>
                    {getDayLabel(d.date, i, lang)}
                  </div>
                ))}
                {weather.forecast.map(d => (
                  <div key={`em-${d.date}`} style={{ fontSize: 14, lineHeight: '20px' }}>{d.emoji}</div>
                ))}
                {weather.forecast.map(d => (
                  <div key={`hi-${d.date}`} style={{ fontSize: 10, color: '#fff' }}>{d.tempMax}°</div>
                ))}
                {weather.forecast.map(d => (
                  <div key={`lo-${d.date}`} style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{d.tempMin}°</div>
                ))}
                {hasRain && weather.forecast.map(d => (
                  <div key={`rn-${d.date}`} style={{ fontSize: 9, color: '#7ec8ff' }}>
                    {d.precipitation > 0 ? `${d.precipitation}` : ''}
                  </div>
                ))}
              </div>
              {hasRain && (
                <Typography variant="caption" sx={{ color: 'rgba(126,200,255,0.4)', fontSize: 9, display: 'block', textAlign: 'right', mt: 0.3 }}>
                  mm
                </Typography>
              )}
            </>
          )}
        </div>
      </Paper>

      {/* ── Right-click context menu ── */}
      <Popover
        open={!!ctxAnchor}
        onClose={closeCtx}
        container={container ?? undefined}
        anchorReference="anchorPosition"
        anchorPosition={ctxAnchor ? { top: ctxAnchor.y, left: ctxAnchor.x } : undefined}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            background: 'rgba(15,25,50,0.97)',
            border: '1px solid rgba(155,89,182,0.5)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2, minWidth: 250, maxWidth: 300, color: '#fff',
          },
        }}
      >
        {/* ── Step 1: select plan ── */}
        {ctxPlanId === null ? (
          <>
            <div style={{ padding: '8px 12px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <RouteIcon sx={{ color: '#c39bd3', fontSize: 15 }} />
              <Typography sx={{ fontSize: 12, color: '#c39bd3', fontWeight: 600 }}>
                {isZh ? '加入旅程計畫' : 'Add to Trip Plan'}
              </Typography>
            </div>
            {propPlans.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', px: 2, pb: 1.5 }}>
                {isZh ? '尚無計畫，請先開啟旅程面板新增' : 'No plans yet. Open Trip Panel to create one.'}
              </Typography>
            ) : (
              <List dense disablePadding sx={{ maxHeight: 220, overflowY: 'auto' }}>
                {propPlans.map(plan => {
                  const count = (plan.attractions ?? []).filter(a => a.id === attraction.id).length;
                  return (
                    <ListItemButton key={plan.id} onClick={() => handleSelectPlan(plan.id)}
                      sx={{ py: 0.5, '&:hover': { background: 'rgba(155,89,182,0.15)' } }}>
                      <ListItemText
                        primary={plan.name}
                        secondary={count > 0
                          ? `✓ ${count} ${isZh ? '次收錄' : 'time(s) added'}`
                          : `${Array.from(new Set((plan.attractions ?? []).map(a => a.date))).length} ${isZh ? '天' : 'days'} · ${(plan.attractions ?? []).length} ${isZh ? '景點' : 'spots'}`}
                        primaryTypographyProps={{ fontSize: 13, color: '#fff' }}
                        secondaryTypographyProps={{
                          fontSize: 10,
                          color: count > 0 ? 'rgba(155,89,182,0.8)' : 'rgba(255,255,255,0.3)',
                        }}
                      />
                      <Typography sx={{ fontSize: 11, color: 'rgba(155,89,182,0.6)' }}>›</Typography>
                    </ListItemButton>
                  );
                })}
              </List>
            )}
            {onOpenTripPanel && (
              <>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                <ListItemButton onClick={() => { closeCtx(); onOpenTripPanel(); }}
                  sx={{ py: 0.8, '&:hover': { background: 'rgba(155,89,182,0.15)' } }}>
                  <ListAltIcon sx={{ fontSize: 16, color: '#c39bd3', mr: 1 }} />
                  <ListItemText
                    primary={isZh ? '旅程面板' : 'Trip Panel'}
                    primaryTypographyProps={{ fontSize: 13 }}
                  />
                </ListItemButton>
              </>
            )}
          </>
        ) : (
          /* ── Step 2: date + transport ── */
          <>
            <div style={{ padding: '8px 12px 6px', display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <IconButton size="small" onClick={() => setCtxPlanId(null)} sx={{ color: 'rgba(255,255,255,0.5)', p: '2px' }}>
                <ArrowBackIcon sx={{ fontSize: 15 }} />
              </IconButton>
              <RouteIcon sx={{ color: '#c39bd3', fontSize: 14 }} />
              <Typography sx={{ fontSize: 12, color: '#c39bd3', fontWeight: 600, flex: 1 }} noWrap>
                {currentPlan?.name}
              </Typography>
            </div>

            <div style={{ padding: '10px 12px' }}>
              {/* Existing dates as quick-select chips */}
              {existingDates.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', mb: 0.5 }}>
                    {isZh ? '已有日期' : 'Existing dates'}
                  </Typography>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {existingDates.map(d => (
                      <button key={d} onClick={() => setCtxDate(d)} style={{
                        background: ctxDate === d ? 'rgba(155,89,182,0.45)' : 'rgba(255,255,255,0.06)',
                        border: ctxDate === d ? '1px solid rgba(155,89,182,0.9)' : '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 4, padding: '2px 8px', cursor: 'pointer',
                        color: '#fff', fontSize: 11,
                      }}>{d}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Date input */}
              <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', mb: 0.5 }}>
                {isZh ? '前往日期' : 'Visit Date'}
              </Typography>
              <input
                type="date"
                value={ctxDate}
                onChange={e => setCtxDate(e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box', marginBottom: 10,
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(155,89,182,0.4)', borderRadius: 4,
                  padding: '5px 8px', color: '#fff', fontSize: 12, colorScheme: 'dark',
                }}
              />

              {/* Transport */}
              <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', mb: 0.5 }}>
                {isZh ? '交通工具（選填）' : 'Transport (optional)'}
              </Typography>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
                {TRANSPORT_OPTIONS.map(opt => (
                  <button key={opt.key}
                    onClick={() => setCtxTransport(ctxTransport === opt.key ? '' : opt.key)}
                    style={{
                      background: ctxTransport === opt.key ? 'rgba(155,89,182,0.45)' : 'rgba(255,255,255,0.06)',
                      border: ctxTransport === opt.key ? '1px solid rgba(155,89,182,0.9)' : '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 6, padding: '4px 8px', cursor: 'pointer',
                      color: '#fff', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3,
                    }}
                  >
                    {opt.emoji} {isZh ? opt.zh : opt.en}
                  </button>
                ))}
              </div>

              <button onClick={handleConfirmAdd} disabled={!ctxDate || ctxLoading} style={{
                width: '100%',
                background: ctxDate && !ctxLoading ? 'linear-gradient(135deg, #9b59b6, #c39bd3)' : 'rgba(155,89,182,0.25)',
                border: 'none', borderRadius: 6, padding: '7px',
                cursor: ctxDate && !ctxLoading ? 'pointer' : 'not-allowed',
                color: '#fff', fontSize: 13, fontWeight: 600,
              }}>
                {ctxLoading ? '⏳ ' : '✓ '}{isZh ? '加入行程' : 'Add to Itinerary'}
              </button>
            </div>
          </>
        )}
      </Popover>
    </>
  );
};

export default AttractionDetailCard;
